import { findHardware, type DeviceType, type HardwareId } from "@/contracts";

/**
 * What the supported-devices page says about each piece of hardware.
 *
 * The registry in `@/contracts` decides what may be registered; this decides
 * what the page tells you about it. They are separate because the API has no
 * business carrying marketing copy, and joined by `id` — with a test that
 * every registry entry has a page entry, so the screen cannot fall behind
 * what the API will accept.
 *
 * The source of truth for the claims below is `docs/DEVICE_EM300_TH.md`, and
 * the division is the point of the file. `confirmed` is what a label or a
 * listing says and may be repeated with its source attached; `claimed` is
 * marketing copy, true only in the sense that somebody published it;
 * `platformBehaviour` is our own code and is the only part we can vouch for;
 * `toVerify` is everything nobody has checked. Anything without a source
 * belongs in `toVerify`.
 */

export interface Specification {
  label: string;
  value: string;
  /** Who is making the claim. Omitted only when the claim is our own code. */
  source?: string;
}

/** A manufacturer's feature claim, with what it actually means for us. */
export interface Claim {
  feature: string;
  soWhat: string;
}

export interface OpenQuestion {
  topic: string;
  why: string;
  /** Who can actually answer it. Not us. */
  answeredBy: string;
}

/** Why a documented device cannot be registered, and what it would take. */
export interface PendingSupport {
  reason: string;
  blockedOn: string;
}

interface CommonEntry {
  manufacturer: string;
  model: string;
  /** The reseller's variant name, so a purchase order matches. */
  listingName: string;
  sku: string;
  reseller: string;
  listingUrl: string;
  /**
   * Local product photograph from the Semakiot (or manufacturer) listing.
   * Illustration only — not installation proof and not a verified form factor.
   */
  imageSrc: string;
  summary: string;
  /**
   * Whether anyone has put an actual unit in front of this system. Per entry
   * rather than per page, so the page stops making a blanket claim the day
   * one of them is finally tested.
   */
  verifiedOnHardware: boolean;
  confirmed: readonly Specification[];
  claimed: readonly Claim[];
  platformBehaviour: readonly Specification[];
  toVerify: readonly OpenQuestion[];
}

/** Hardware the API will let you register. */
interface SupportedEntry extends CommonEntry {
  /** Joins this entry to its registry entry in `@/contracts`. */
  id: HardwareId;
  /** What it measures. Taken from the registry, never written here. */
  deviceType: DeviceType;
  pending?: never;
}

/**
 * Hardware described here and absent from the registry, so the page can say
 * "we do not ingest this yet" instead of implying we do.
 *
 * It carries no `deviceType` — there is no registry entry to take one from,
 * and inventing one would put a measurement on the page that the device does
 * not make. A test checks the flag against the registry in both directions,
 * which is what stops the two from drifting apart (ADR-060).
 */
interface PendingEntry extends CommonEntry {
  id: string;
  deviceType?: never;
  pending: PendingSupport;
}

export type CatalogueEntry = SupportedEntry | PendingEntry;

const SEMAKIOT_LISTING = "Semakiot listing";
const MILESIGHT_DATASHEET = "Milesight datasheet";

// Non-null: the id is typed as a registry key, so these cannot miss.
const em300th = findHardware("milesight-em300-th")!;
const em500pt100 = findHardware("milesight-em500-pt100-t200")!;
const em410rdl = findHardware("milesight-em410-rdl")!;
const vs370 = findHardware("milesight-vs370")!;
const em300sld = findHardware("milesight-em300-sld")!;
const em300mcs = findHardware("milesight-em300-mcs")!;
const uc300 = findHardware("milesight-uc300")!;
const ct103 = findHardware("milesight-ct103")!;

export const CATALOGUE: readonly CatalogueEntry[] = [
  {
    id: "milesight-em300-th",
    manufacturer: em300th.manufacturer,
    model: em300th.model,
    listingName: "EM300-TH-915M",
    sku: "MO20042",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/em300-th-915m/",
    imageSrc: "/marketing/device-temp-humidity.jpg",
    summary:
      "A compact, battery-powered environmental sensor that measures temperature and humidity and reports over LoRaWAN. Intended for indoor and outdoor use — offices, hospitals, factories, greenhouses — and the pilot unit for this deployment.",
    deviceType: em300th.type,
    verifiedOnHardware: true,
    confirmed: [
      { label: "Manufacturer", value: em300th.manufacturer },
      { label: "Model", value: em300th.model },
      {
        label: "Variant sold locally",
        value: "EM300-TH-915M, the 915 MHz version",
        source: SEMAKIOT_LISTING,
      },
      { label: "Reseller part number", value: "MO20042", source: SEMAKIOT_LISTING },
      { label: "Measures", value: "Temperature and humidity" },
      { label: "Radio", value: "LoRaWAN" },
      { label: "Enclosure", value: "IP67, UV-resistant", source: SEMAKIOT_LISTING },
      { label: "Power", value: "4000 mAh battery, replaceable", source: SEMAKIOT_LISTING },
      {
        label: "Decoded temperature field",
        value: "temperature",
        source: "Hardware test, 2026-10-31",
      },
      {
        label: "Reporting interval of the units tested",
        value: "Every 5 minutes",
        source: "Hardware test, 2026-10-31",
      },
    ],
    claimed: [
      {
        feature: "Sensirion sensing element",
        soWhat:
          "A credible supplier, but the listing gives no measurement range and no accuracy figure, which is what actually decides whether it can watch a freezer.",
      },
      {
        feature: "Up to 10 km line of sight",
        soWhat:
          "Line of sight. A freezer is the opposite of line of sight, so treat this as an upper bound for an open field and nothing else.",
      },
      {
        feature: "Battery lasts several years",
        soWhat:
          "The units tested report every five minutes. The listing still does not turn that into a battery-life number. The useful half of the claim is that the battery is replaceable, so the pilot stays serviceable.",
      },
      {
        feature: "Stores records locally and retransmits them",
        soWhat:
          "Gaps can be backfilled after an outage. It also means a replayed reading may arrive with the wrong time — see the open questions.",
      },
      {
        feature: "NFC configuration",
        soWhat: "Setup happens at the device, in person, with a phone. No gateway needed for it.",
      },
      {
        feature: "Works with standard LoRaWAN gateways and network servers",
        soWhat:
          "The Things Stack should work, and the device is not locked to the manufacturer's cloud.",
      },
      {
        feature: "Milesight D2D and Milesight IoT Cloud",
        soWhat: "Not used by this platform. Listed so nobody assumes we depend on either.",
      },
    ],
    platformBehaviour: [
      { label: "Device type to register", value: "Temperature and humidity" },
      { label: "How readings arrive", value: "The Things Stack webhook, or the simulator" },
      {
        label: "Readings normalised",
        value: "Temperature, humidity, battery, RSSI and SNR, when the uplink carries them",
      },
      {
        label: "Decoded field names",
        value:
          "Temperature is read from temperature, confirmed on a real uplink. Humidity and battery still use the candidate lists, overridable with LORAWAN_DECODED_FIELDS",
      },
      {
        label: "Reporting interval",
        value: "5 minutes on the units tested, with a device counted as not reporting after 15",
      },
      {
        label: "Alert thresholds",
        value: "Taken from the target range of the asset the device is attached to",
      },
      {
        label: "Duplicate uplinks",
        value: "Discarded by frame counter, so a retransmission cannot be counted twice",
      },
      {
        label: "Measurement time",
        value: "Inferred from gateway time, because no payload timestamp has been confirmed",
      },
    ],
    toVerify: [
      {
        topic: "Measurement range and accuracy",
        why: "Decides whether it can be trusted to watch a freezer at -20 °C at all. The listing names the sensor supplier but gives no figures",
        answeredBy: "Milesight datasheet",
      },
      {
        topic: "Whether the payload carries its own timestamp",
        why: "The device buffers and replays records, and a replay arrives with the gateway time of the replay. Without a payload timestamp, Tuesday's excursion is filed as Thursday's",
        answeredBy: "The codec, once available",
      },
      {
        topic: "Which 915 MHz plan, and which sub-band",
        why: "The part number says 915 MHz, which narrows it, but US915 and AU915 both live there and the sub-band still has to match the gateway",
        answeredBy: "Milesight and Semakiot",
      },
      {
        topic: "IP67 in a wash-down environment",
        why: "The rating is confirmed, but a rating is not a warranty for hot water and detergent",
        answeredBy: "Milesight and Semakiot",
      },
      {
        topic: "Behaviour at low temperature",
        why: "Battery capacity falls in the cold, and the listing's examples are offices and greenhouses rather than freezers",
        answeredBy: "Datasheet, then measured",
      },
      {
        topic: "External probe support",
        why: "Whether the sensor body can sit outside the cold space",
        answeredBy: "Milesight datasheet",
      },
      {
        topic: "Remote configuration",
        why: "NFC covers setup in person; changing the reporting interval over the air is a different question",
        answeredBy: "Milesight datasheet",
      },
      {
        topic: "Certifications, pricing and availability",
        why: "Commercial commitments. The listing price is a placeholder and the stock state contradicts itself, so neither may be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-em500-pt100-t200",
    manufacturer: em500pt100.manufacturer,
    model: em500pt100.model,
    listingName: "EM500-PT100-915M-T200",
    sku: "MO20038",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/em500-pt100-915m-t200/",
    imageSrc: "/marketing/device-pt100.jpg",
    summary:
      "A battery-powered LoRaWAN temperature sensor with an external PT100 probe. Semakiot lists the 915 MHz T200 variant (SKU MO20038), whose probe is specified for -50 °C to 200 °C. The transceiver stays in ambient air; only the probe goes into the pipe, oven or machine. It does not measure humidity. Public storefront price remains the Semakiot placeholder; do not quote it.",
    deviceType: em500pt100.type,
    verifiedOnHardware: false,
    confirmed: [
      { label: "Manufacturer", value: em500pt100.manufacturer },
      { label: "Model", value: em500pt100.model },
      {
        label: "Variant sold locally",
        value: "EM500-PT100-915M-T200",
        source: "Semakiot storefront, 2026-10-28",
      },
      { label: "Reseller part number", value: "MO20038", source: "Semakiot storefront schema" },
      { label: "Measures", value: "Temperature at an external PT100 probe. Not humidity" },
      {
        label: "Probe range (T200)",
        value: "-50 °C to 200 °C, resolution 0.1 °C, 3-wire, class A, typical ±0.6 °C",
        source: MILESIGHT_DATASHEET,
      },
      {
        label: "Probe",
        value: "304 stainless steel, 4×30 mm, IP67, Teflon cable rated -200 °C to 290 °C",
        source: MILESIGHT_DATASHEET,
      },
      {
        label: "Transceiver operating temperature",
        value: "-30 °C to 70 °C. The enclosure does not follow the probe into the process",
        source: MILESIGHT_DATASHEET,
      },
      {
        label: "Radio profile",
        value:
          "LoRaWAN Class A, Device Repository em500-pt100, hardware V1.x, firmware 2.x, profile-au915",
        source: "The Things Network Device Repository",
      },
      {
        label: "Power",
        value: "19000 mAh ER34615 Li-SOCl2 battery, replaceable",
        source: MILESIGHT_DATASHEET,
      },
    ],
    claimed: [
      {
        feature: "Up to 10 km line of sight",
        soWhat:
          "Line of sight, from the Semakiot listing. A metal cabinet or a plant room is not an open field, so treat the figure as an upper bound and survey the site.",
      },
      {
        feature: "10 years on the 19000 mAh battery",
        soWhat:
          "Milesight's figure is at a 10-minute interval and 25 °C, in a lab. This platform assumes a five-minute report and marks the device offline after fifteen minutes, which is a different battery budget.",
      },
      {
        feature: "Stores 1,000 records and retransmits them",
        soWhat:
          "Gaps can be backfilled after an outage. A replayed reading can still be filed under gateway time, because this platform does not apply the history timestamp inside the payload.",
      },
      {
        feature: "NFC and USB Type-C configuration",
        soWhat:
          "Setup is done at the device, in person. Changing the interval over the air is documented by Milesight and is not something this admin sends.",
      },
      {
        feature: "Milesight D2D and Milesight IoT Cloud",
        soWhat:
          "Not used by this platform. Listed so nobody assumes the sensor has to live in the manufacturer's cloud.",
      },
    ],
    platformBehaviour: [
      { label: "Device type to register", value: "Temperature" },
      { label: "How readings arrive", value: "The Things Stack webhook, or the simulator" },
      {
        label: "Readings normalised",
        value:
          "Temperature and battery, plus RSSI and SNR, when the uplink carries them. Humidity is not expected",
      },
      {
        label: "Decoded field names",
        value:
          "The published Milesight decoder emits temperature and battery, which are already in the default candidate list. Unverified against a ZonaClara uplink",
      },
      {
        label: "Assumed reporting interval",
        value:
          "5 minutes, with a device counted as not reporting after 15. That is the platform default, not this sensor's factory interval",
      },
      {
        label: "Alert thresholds",
        value: "Taken from the target temperature range of the asset the device is attached to",
      },
      {
        label: "Measurement time",
        value:
          "Inferred from gateway time. History frames in the payload carry their own timestamp and are not used as the reading time",
      },
    ],
    toVerify: [
      {
        topic: "Which 915 MHz plan and sub-band (AU915 for Argentina)",
        why: "The Semakiot slug says 915M. US915 and AU915 both fit that name, and the purchase order must name AU915 sub-band 2",
        answeredBy: "Semakiot",
      },
      {
        topic: "Ingress protection of the transceiver",
        why: "The datasheet prose says IP67. The Device Repository ipCode field says IP66. The T200 probe line in the datasheet says IP67 for the probe itself",
        answeredBy: "Milesight datasheet, checked against the unit",
      },
      {
        topic: "What a failed or out-of-range probe looks like once decoded",
        why: "The protocol uses FFFF for a collection failure and FFFD for out of range. The published decoder turns those into -0.1 °C and -0.3 °C, which this platform cannot tell apart from a real reading",
        answeredBy: "A real uplink",
      },
      {
        topic: "Whether a replayed history record is filed under the right time",
        why: "The decoder puts a unix timestamp on history entries. Ingestion still uses gateway time for the reading",
        answeredBy: "A real uplink",
      },
      {
        topic: "Factory reporting interval",
        why: "The battery claim assumes ten minutes. The offline window here assumes five. The unit has to be read",
        answeredBy: "The unit, then Milesight",
      },
      {
        topic: "Cable length on the T200 that Semakiot ships",
        why: "The series page says 1.5 m and also says customizable. The box has to be checked",
        answeredBy: "Semakiot",
      },
      {
        topic: "RAMATEL registration",
        why: "A legal requirement for equipment in these bands, not a formality",
        answeredBy: "Semakiot, or whoever imports",
      },
      {
        topic: "Pricing and availability",
        why: "The storefront price is a placeholder. None of it may be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-em410-rdl",
    manufacturer: em410rdl.manufacturer,
    model: em410rdl.model,
    listingName: "EM410-RDL-915M Radar Distance/Level Sensor",
    sku: "MO08108",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/em410-rdl-915m/",
    imageSrc: "/marketing/device-level.jpg",
    summary:
      "A non-contact millimetre-wave radar that measures the distance between itself and the surface of a liquid, and reports it over LoRaWAN. It is mounted above what it watches and never touches it, which is what makes it usable on hazardous or corrosive contents. The listing names sewers, rivers, reservoirs and storage tanks as its intended settings.",
    deviceType: em410rdl.type,
    verifiedOnHardware: false,
    confirmed: [
      { label: "Manufacturer", value: em410rdl.manufacturer },
      { label: "Model", value: em410rdl.model },
      {
        label: "Variant sold locally",
        value: "EM410-RDL-915M, the 915 MHz version",
        source: SEMAKIOT_LISTING,
      },
      { label: "Reseller part number", value: "MO08108", source: SEMAKIOT_LISTING },
      { label: "Measures", value: "Distance to a liquid surface" },
      { label: "Radio", value: "LoRaWAN" },
      {
        label: "Measuring range",
        value: "0.3 m to 12 m, with a blind zone near the sensor",
        source: SEMAKIOT_LISTING,
      },
      { label: "Radar beam angle", value: "8°", source: SEMAKIOT_LISTING },
      {
        label: "Enclosure",
        value: "IP68, PVDF body, rated for 1 m of fresh water for up to 48 hours",
        source: SEMAKIOT_LISTING,
      },
      {
        label: "Mounting",
        value:
          'Threaded, in G 1" or NPT 1", with internal or external antenna depending on the part number suffix',
        source: SEMAKIOT_LISTING,
      },
    ],
    claimed: [
      {
        feature: "Millimetre-wave radar, unaffected by dust, condensation, temperature or noise",
        soWhat:
          "The reason to choose it over an ultrasonic sensor: the failure modes of a tank — vapour above the liquid, condensation on the sensor — are the ones this claims to ignore. No accuracy figure is given, so how closely it measures is still unknown.",
      },
      {
        feature: "Works on high-salt contents, including seawater and concentrated brine",
        soWhat:
          "Widens what the platform could be sold against, but the same listing gives no accuracy figure, so it is a claim about survival rather than about precision.",
      },
      {
        feature: "Non-invasive: never touches the contents",
        soWhat:
          "Means a sensor can be added to a tank that is already in service, and that hazardous contents do not rule it out. It also means the reading is a distance, which is why this platform stores a distance.",
      },
      {
        feature: "Built-in three-axis accelerometer for tilt",
        soWhat:
          "A tilted radar measures a longer, wrong distance. Milesight exposes tilt, but this platform ingests no tilt field today, so a knocked sensor would read as a falling level.",
      },
      {
        feature: "Blind-zone alarm and one-click echo-curve calibration",
        soWhat:
          "Device-side features, configured at the sensor. Nothing in this platform drives them, and a blind-zone alarm is not one of the incidents it raises.",
      },
      {
        feature: "Local storage of historical records with retransmission",
        soWhat:
          "Gaps can be backfilled after an outage — and, as with the EM300-TH, a replayed reading may arrive stamped with the time of the replay. See the open questions.",
      },
      {
        feature: "NFC and Bluetooth configuration",
        soWhat: "Setup happens at the device, in person. No gateway needed for it.",
      },
      {
        feature: "ATEX/IECEx certified option for explosive atmospheres",
        soWhat:
          "An option on some variant, per the listing. Which part number carries it, and whether the 915 MHz one does, is not stated and must not be repeated to a customer as though it were.",
      },
      {
        feature: "Milesight Development Platform, D2D and OTA updates",
        soWhat: "Not used by this platform. Listed so nobody assumes we depend on them.",
      },
    ],
    platformBehaviour: [
      { label: "Device type to register", value: "Distance and level" },
      { label: "How readings arrive", value: "The Things Stack webhook, or the simulator" },
      {
        label: "Readings normalised",
        value: "Distance, battery, RSSI and SNR, when the uplink carries them",
      },
      {
        label: "Unit stored and displayed",
        value: "Millimetres everywhere, with no conversion at any point",
      },
      {
        label: "What is stored",
        value:
          "The distance from the sensor to the contents, not a level. Level is tank depth minus distance, and the platform does not model the mounting geometry needed to work it out",
      },
      {
        label: "Alert thresholds",
        value:
          "A minimum and maximum distance on the asset, in millimetres. The minimum is how close the contents may come to the sensor, so crossing it means too full",
      },
      {
        label: "Incidents raised",
        value:
          "Shown as 'Level low' when the distance goes above the maximum, and 'Level high' when it drops below the minimum",
      },
      {
        label: "Decoded field names",
        value: "Guessed as `distance`, overridable with LORAWAN_DECODED_FIELDS",
      },
      {
        label: "Tilt, blind-zone alarms and echo curves",
        value: "Not ingested. The platform reads a distance and nothing else from this sensor",
      },
    ],
    toVerify: [
      {
        topic: "What unit the codec reports the distance in",
        why: "The single most damaging unknown here. We store millimetres; a codec emitting metres would record a 2 m reading as 2 mm and hold every tank permanently below its floor. The listing quotes the range in metres and says nothing about the payload",
        answeredBy: "A real uplink, or the Milesight codec",
      },
      {
        topic: "Measurement accuracy",
        why: "Decides how tight a threshold is worth setting. The listing claims stability and immunity to dust and condensation but gives no figure at all",
        answeredBy: "Milesight datasheet",
      },
      {
        topic: "How large the blind zone actually is",
        why: "The listing says there is one and calls it small without measuring it. It is the region where a full tank stops being readable, so it decides where the sensor may be mounted",
        answeredBy: "Milesight datasheet",
      },
      {
        topic: "What the device sends when the reading is inside the blind zone",
        why: "If it reports a floor value rather than nothing, an overfull tank would look like a merely full one, and our minimum-distance alert would never fire",
        answeredBy: "The codec, once available",
      },
      {
        topic: "Which part numbers carry the ATEX/IECEx option",
        why: "The listing mentions the certification without tying it to a variant. Repeating it against the wrong part number would be a safety claim we cannot support",
        answeredBy: "Milesight and Semakiot",
      },
      {
        topic: "Whether the payload carries its own timestamp",
        why: "Same retransmission problem as the EM300-TH: a replayed record filed under the time of its replay puts last night's empty tank on this morning's chart",
        answeredBy: "The codec, once available",
      },
      {
        topic: "Default reporting interval and battery life",
        why: "Drives the offline threshold. The listing states neither, and unlike the EM300-TH it does not even claim a battery capacity",
        answeredBy: "Datasheet, then measured on a real unit",
      },
      {
        topic: "Which 915 MHz plan, and which sub-band",
        why: "US915 and AU915 both live at 915 MHz, and the sub-band still has to match the gateway",
        answeredBy: "Milesight and Semakiot",
      },
      {
        topic: "Whether tilt is reported in the uplink or only read over NFC",
        why: "A knocked sensor measures a longer distance, which this platform would show as a draining tank. If tilt is in the payload it is worth ingesting",
        answeredBy: "The codec, once available",
      },
      {
        topic: "Certifications, pricing and availability",
        why: "Commercial commitments, and the listing price is a placeholder. None of it may be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-vs370",
    manufacturer: vs370.manufacturer,
    model: vs370.model,
    listingName: "VS370-915M Radar Human Presence Sensor",
    sku: "MO20068",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/vs370-915m-radar-human-presence-sensor/",
    imageSrc: "/marketing/device-presence.jpg",
    summary:
      "A millimetre-wave radar and PIR sensor that detects whether a person is in a room, without capturing an image. The listing aims it at meeting rooms and conference rooms. It reports one bit — occupied or not — and what makes that an alert is the hour rather than the reading: somebody here at 11am is the room working, somebody here at 3am is not. The comparison comes from expected-occupancy hours set on the asset, and a sensor watching an asset without them is recorded and never judged.",
    deviceType: vs370.type,
    verifiedOnHardware: false,
    confirmed: [
      { label: "Manufacturer", value: "Milesight" },
      { label: "Model", value: "VS370" },
      {
        label: "Variant sold locally",
        value: "VS370-915M, the 915 MHz version",
        source: SEMAKIOT_LISTING,
      },
      { label: "Reseller part number", value: "MO20068", source: SEMAKIOT_LISTING },
      { label: "Detects", value: "Human presence and movement", source: SEMAKIOT_LISTING },
      { label: "Radio", value: "LoRaWAN" },
      {
        label: "Sensing elements",
        value: "Millimetre-wave radar combined with PIR",
        source: SEMAKIOT_LISTING,
      },
      {
        label: "Mount",
        value: "Detachable magnetic mount with an extra 30° of deflection",
        source: SEMAKIOT_LISTING,
      },
      // The two numbers that settle whether this device could ever serve the
      // cold-chain customers the rest of the catalogue is aimed at. It
      // cannot: a freezer is below its minimum and a cold room condenses on
      // an IP30 enclosure. The platform can read this sensor now; that is a
      // different question from whether it belongs on a customer's site.
      {
        label: "Operating range",
        value: "0 °C to +30 °C, so not a cold room and not outdoors",
        source: MILESIGHT_DATASHEET,
      },
      {
        label: "Enclosure",
        value: "IP30, indoor only — no protection against water",
        source: MILESIGHT_DATASHEET,
      },
    ],
    claimed: [
      {
        feature: "Captures no images, so privacy is preserved",
        soWhat:
          "The commercially important claim: it is what makes presence sensing acceptable in a room people work in. It is also the reason the reading is a single bit, and the reason this platform stores one bit and not a head count.",
      },
      {
        feature: "Detects the slightest movement and produces mobility statistics",
        soWhat:
          "Two different outputs, and the listing gives figures for neither. Whether 'statistics' means occupancy counts, dwell time or something else is unknown, so none of it is stored: only the occupied flag is, and a payload carrying a count instead shows up as a gap rather than as a number we guessed the meaning of.",
      },
      {
        feature: "Works in low light or complete darkness",
        soWhat: "Radar and PIR do not need light. Removes a whole class of deployment constraint.",
      },
      {
        feature: "Configurable PIR and radar sensitivity",
        soWhat:
          "Tuning happens at the device, not in this platform. A false-positive problem would be solved on the sensor.",
      },
      {
        feature: "Milesight D2D for direct, gateway-free control of lights and HVAC",
        soWhat:
          "A genuinely useful feature and entirely outside this platform: D2D traffic never reaches a network server, so we would never see it.",
      },
      {
        feature: "Custom hibernation periods to save battery",
        soWhat:
          "Relevant to the offline threshold. A sensor that is deliberately asleep must not be reported as one that has failed.",
      },
      {
        feature: "NFC and Bluetooth configuration, OTA updates, Milesight Development Platform",
        soWhat:
          "Setup and updates happen outside this platform, as with the other Milesight units.",
      },
    ],
    platformBehaviour: [
      { label: "Device type to register", value: "Presence" },
      {
        label: "Stored per reading",
        value: "Occupied or not, as one bit, plus the battery level. No count and no dwell time",
      },
      {
        label: "What raises an alert",
        value:
          "Somebody present for the rule's whole duration at a time the asset says it should be empty. Ten minutes by default, which is what stops a cleaner walking past from ringing a phone",
      },
      {
        label: "Without expected-occupancy hours on the asset",
        value:
          "Readings are stored and never judged, the same as a freezer with no target range. The asset page is where the hours are set",
      },
      {
        label: "If the codec never reports an empty room",
        value:
          "An open incident would not close on its own, because nothing would contradict it. This is the first thing to check against a real uplink",
      },
    ],
    toVerify: [
      {
        topic: "Detection range and coverage angle",
        why: "Decides how many sensors a room needs, and the listing gives no figure for either",
        answeredBy: "Milesight datasheet",
      },
      {
        topic: "What the payload actually contains, and under which key",
        why: "The platform now reads an occupied flag from a short list of candidate key names, none of which has been seen in a real VS370 payload. A codec using another name, or reporting a person count instead of a flag, produces a dead-lettered uplink rather than a wrong reading \u2014 visible, but still a device that does not work",
        answeredBy: "The codec, once available",
      },
      {
        topic:
          "Whether it reports on change or on an interval, and whether it reports an empty room at all",
        why: "Two problems in one. A sensor that only speaks when the room changes state looks identical to a dead one, which breaks the offline sweep. And if it never transmits the empty state, a presence incident has nothing to resolve it and stays open until somebody closes it by hand",
        answeredBy: "Milesight datasheet, then a real uplink",
      },
      {
        topic: "Power source and battery life",
        why: "The listing mentions battery saving but never says what powers it. Mains-powered and battery-powered sensors need different offline thresholds",
        answeredBy: "Milesight datasheet",
      },
      {
        topic: "Which 915 MHz plan, and which sub-band",
        why: "Same question as every other unit here: 915 MHz narrows it without settling it",
        answeredBy: "Milesight and Semakiot",
      },
      {
        topic: "Whether the expected-occupancy model matches how a room is actually used",
        why: "The platform judges presence against a weekly schedule of days and hours, which fits an office and fits a stockroom nobody should enter. A space booked ad hoc, or one with a rota that changes week to week, has no schedule to compare against",
        answeredBy: "Semakiot, and the first customer who deploys one",
      },
      {
        topic: "Certifications, pricing and availability",
        why: "Commercial commitments. None of it may be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-em300-sld",
    manufacturer: em300sld.manufacturer,
    model: em300sld.model,
    listingName: "EM300-SLD Spot Leak Detection Sensor",
    sku: "to verify (Semakiot — not on public storefront as of 2026-10-23)",
    reseller: "Semakiot",
    listingUrl: "https://www.milesight.com/iot/product/lorawan-sensor/em300-sld-zld",
    imageSrc: "/marketing/device-leak.jpg",
    summary:
      "A compact spot leak sensor with a stainless probe that detects conducting liquid (about 5 mm at the tip) and reports over LoRaWAN. Milesight's product page also describes the EM300-ZLD rope/zone sibling — that model is not in our registry. The platform stores one leak bit — wet or dry — and opens an incident when the probe stays wet for the rule's duration. Semakiot lists other EM300 units publicly but had no EM300-SLD storefront page on 2026-10-23 — AU915 SKU and stock remain to verify on a purchase order.",
    deviceType: em300sld.type,
    verifiedOnHardware: false,
    confirmed: [
      { label: "Manufacturer", value: "Milesight" },
      { label: "Model", value: "EM300-SLD (probe / spot)" },
      {
        label: "Manufacturer product page",
        value: "milesight.com/iot/product/lorawan-sensor/em300-sld-zld",
        source: "Milesight product page",
      },
      {
        label: "Regional plans listed by Milesight",
        value: "Includes AU915 and US915 among others",
        source: "Milesight product page",
      },
      {
        label: "Detects",
        value: "Conducting liquid at a spot probe (trigger ~5 mm); cable typically 1.5 m",
        source: "Milesight product page",
      },
      {
        label: "Also measures",
        value: "Temperature (−30 °C to +70 °C) and humidity",
        source: "Milesight product page",
      },
      { label: "Radio", value: "LoRaWAN Class A", source: "Milesight product page" },
      {
        label: "Enclosure",
        value: "IP67 UV-resistant transceiver; stainless probe",
        source: "Milesight product page",
      },
      {
        label: "Power",
        value: "ER18505 Li-SOCl2, 4000 mAh (8000 mAh optional)",
        source: MILESIGHT_DATASHEET,
      },
    ],
    claimed: [
      {
        feature: "Spot leak detection for pipe bases, basements and pump failures",
        soWhat:
          "The commercial use cases Milesight names. This platform alerts on the bit; it does not locate the leak or control a pump.",
      },
      {
        feature: "Integrated temperature and humidity",
        soWhat:
          "Decoded when the codec carries them. Leak is the metric that raises LEAK_DETECTED; climate alerts still use the organisation temperature/humidity rules if those readings arrive.",
      },
      {
        feature: "NFC configuration and long battery life",
        soWhat: "Setup happens outside this platform, as with the other Milesight EM300 units.",
      },
      {
        feature: "EM300-ZLD rope/zone option on the same product page",
        soWhat:
          "A different SKU (sensing cable, not a spot probe). Not registered here; do not order ZLD expecting SLD behaviour.",
      },
    ],
    platformBehaviour: [
      { label: "Device type to register", value: "Water leak" },
      {
        label: "Stored per reading",
        value: "Leaking or not as one bit, plus battery; temperature and humidity when present",
      },
      {
        label: "What raises an alert",
        value:
          "Every leak-carrying reading across the rule's duration says wet. Seeded default is two minutes at CRITICAL",
      },
      {
        label: "If the codec never reports dry",
        value:
          "An open incident would not close on its own, because null is not evidence. Check against a real uplink first",
      },
    ],
    toVerify: [
      {
        topic: "Semakiot listing, SKU and AU915 stock",
        why: "Manufacturer page is confirmed; local reseller page and Argentine part number are not. The purchase order must name AU915 (or the correct 915 plan for Argentina) and EM300-SLD, not ZLD",
        answeredBy: "Semakiot",
      },
      {
        topic: "Decoded payload key for the leak bit",
        why: "Candidates include leakage / leak / water_leak. Milesight publishes a payload decoder on the product page; none verified on a real unit here",
        answeredBy: "The codec, once available",
      },
      {
        topic: "Whether dry is reported or only omitted when wet",
        why: "Same empty-field risk as presence on The Things Stack",
        answeredBy: "A real uplink",
      },
      {
        topic: "Pricing and lead time",
        why: "Commercial. None of it may be quoted from list prices alone",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-em300-mcs",
    manufacturer: em300mcs.manufacturer,
    model: em300mcs.model,
    listingName: "EM300-MCS-915M Magnetic Contact Switch",
    sku: "MO20043",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/em300-mcs-915m-magnetic-contact-switch/",
    imageSrc: "/marketing/device-door.jpg",
    summary:
      "A LoRaWAN magnetic contact that reports whether a door or window is open, plus temperature and humidity. Semakiot lists the 915M part (SKU MO20043). The platform stores one contact bit — open or closed — and opens an incident when the contact stays open for the rule's duration. Public storefront price remains the Semakiot placeholder; do not quote it.",
    deviceType: em300mcs.type,
    verifiedOnHardware: true,
    confirmed: [
      { label: "Manufacturer", value: "Milesight" },
      { label: "Model", value: "EM300-MCS" },
      {
        label: "Semakiot listing",
        value: "semakiot.com/productos/em300-mcs-915m-magnetic-contact-switch/",
        source: "Semakiot storefront, 2026-10-23",
      },
      { label: "Semakiot SKU", value: "MO20043", source: "Semakiot storefront schema" },
      {
        label: "Detects",
        value: "Magnetic contact open/closed (door or window)",
        source: "Milesight product page",
      },
      {
        label: "Also measures",
        value: "Temperature and humidity",
        source: "Milesight product page",
      },
      { label: "Radio", value: "LoRaWAN Class A, 915M listing", source: "Semakiot listing name" },
      {
        label: "Enclosure",
        value: "IP67 (Milesight EM300 family)",
        source: "Milesight product page",
      },
      {
        label: "Decoded contact fields",
        value: "door and magnet_status",
        source: "Hardware test, 2026-10-31",
      },
      {
        label: "Closed contact on the uplink",
        value: "Reported, not omitted",
        source: "Hardware test, 2026-10-31",
      },
      {
        label: "Reporting interval of the units tested",
        value: "Every 5 minutes",
        source: "Hardware test, 2026-10-31",
      },
    ],
    claimed: [
      {
        feature: "Door and window open/closed with LoRaWAN uplink",
        soWhat:
          "The commercial use for cold rooms and stockrooms. This platform alerts when open lasts for the rule duration; it does not lock doors or dispatch security.",
      },
      {
        feature: "Integrated temperature and humidity",
        soWhat:
          "Decoded when the codec carries them. Door open is the metric that raises DOOR_OPEN; climate alerts still use organisation temperature/humidity rules if those readings arrive.",
      },
      {
        feature: "NFC configuration and long battery life",
        soWhat: "Setup happens outside this platform, as with the other Milesight EM300 units.",
      },
    ],
    platformBehaviour: [
      { label: "Device type to register", value: "Magnetic contact" },
      {
        label: "Stored per reading",
        value: "Open or closed as one bit, plus battery; temperature and humidity when present",
      },
      {
        label: "What raises an alert",
        value:
          "Every contact-carrying reading across the rule's duration says open. Seeded default is five minutes at WARNING",
      },
      {
        label: "Closed contact",
        value:
          "Reported on the uplink. An incident can resolve when the contact shuts, because a missing field is not how these units say closed",
      },
      {
        label: "Reporting interval",
        value: "5 minutes on the units tested, with a device counted as not reporting after 15",
      },
    ],
    toVerify: [
      {
        topic: "Which 915 MHz plan and sub-band (AU915 for Argentina)",
        why: "The Semakiot slug says 915M; the purchase order must name AU915 for Argentina",
        answeredBy: "Semakiot",
      },
      {
        topic: "Real price and lead time",
        why: "Storefront price is a placeholder. None of it may be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-uc300",
    manufacturer: uc300.manufacturer,
    model: uc300.model,
    listingName: "UC300-915M IoT Controller",
    sku: "MO20013",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/uc300-915m/",
    imageSrc: "/marketing/device-controller.jpg",
    summary:
      "An industrial LoRaWAN IoT controller with digital and analog I/O, PT100 inputs and Modbus RS485. Semakiot lists the 915M part (SKU MO20013). The platform stores each interface as a device channel rather than a single sensor reading. Public storefront price remains the Semakiot placeholder; do not quote it. Continuous power and Class C coverage are installation requirements.",
    deviceType: uc300.type,
    verifiedOnHardware: false,
    confirmed: [
      { label: "Manufacturer", value: "Milesight" },
      { label: "Model", value: "UC300" },
      {
        label: "Semakiot listing",
        value: "semakiot.com/productos/uc300-915m/",
        source: "Semakiot storefront, 2026-10-23",
      },
      { label: "Semakiot SKU", value: "MO20013", source: "Semakiot storefront schema" },
      {
        label: "Interfaces",
        value:
          "4× DI, 2× DO (relay), 2× 4–20 mA, 2× 0–10 V, 2× PT100, RS232, RS485 Modbus (up to 32 channels)",
        source: "Milesight datasheet",
      },
      {
        label: "Radio",
        value: "LoRaWAN Class C, 915M listing (AU915 to confirm on PO)",
        source: "Milesight datasheet / Semakiot listing",
      },
      {
        label: "Power",
        value: "5–24 VDC terminal or 5 V USB-C — not battery powered",
        source: "Milesight datasheet",
      },
      {
        label: "Enclosure",
        value: "IP30 metal, indoor, −20 °C to 60 °C",
        source: "Milesight datasheet",
      },
    ],
    claimed: [
      {
        feature: "Wire diverse sensors and actuators into one LoRaWAN node",
        soWhat:
          "Useful as a bridge for 4–20 mA, PT100, dry contacts and Modbus meters. It is not a drop-in fridge sensor.",
      },
      {
        feature: "IF-THEN logic and autonomous action when the network drops",
        soWhat:
          "Configured on the device (ToolBox / Milesight Cloud), not in this admin. This platform ingests uplinks; it does not author UC300 rules yet.",
      },
      {
        feature: "Remote digital-output control over LoRaWAN downlink",
        soWhat:
          "Milesight documents port 85. This admin can queue a DO open/close when TTS credentials are set; without them the action is audited and refused at the Network Server.",
      },
    ],
    platformBehaviour: [
      { label: "Device type to register", value: "IoT controller" },
      {
        label: "Stored per reading",
        value:
          "One ChannelReading per interface key present in the decoded payload (gpio_input_*, pt100_*, adc_*, adv_*, modbus_chn_*, …)",
      },
      {
        label: "What raises an alert",
        value:
          "Device offline, plus opt-in per-channel range or digital-fault rules configured on each DeviceChannel (ADR-082)",
      },
      {
        label: "Codec assumption",
        value: "Official Milesight UC300 decoder keys. Unverified against a real ZonaClara uplink",
      },
    ],
    toVerify: [
      {
        topic: "Which 915 MHz plan and sub-band (AU915 for Argentina)",
        why: "The Semakiot slug says 915M; the purchase order must name AU915 for Argentina",
        answeredBy: "Semakiot",
      },
      {
        topic: "Real TTS uplink from a UC300 on the pilot application",
        why: "Confirms the payload formatter emits the official keys this platform maps",
        answeredBy: "A commissioned unit",
      },
      {
        topic: "Which DI / AI / Modbus registers the site actually wires",
        why: "Channel labels and future alert rules need the site meaning, not just the codec key",
        answeredBy: "The install / ZonaClara",
      },
      {
        topic: "Real price and lead time",
        why: "Storefront price is a placeholder. None of it may be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-ct103",
    manufacturer: ct103.manufacturer,
    model: ct103.model,
    listingName: "CT103-915M",
    sku: "To verify",
    reseller: "Semakiot",
    listingUrl: "https://www.milesight.com/iot/product/lorawan-sensor/ct10x",
    imageSrc: "/marketing/device-current.jpg",
    summary:
      "A clamp that measures the RMS current on one cable, up to 250 A, and reports it over LoRaWAN. It sits inside a cabinet. It does not switch the load and it is not an electricity bill.",
    deviceType: ct103.type,
    verifiedOnHardware: false,
    confirmed: [
      { label: "Manufacturer", value: ct103.manufacturer },
      { label: "Model", value: ct103.model },
      {
        label: "Variant for Argentina",
        value: "CT103-915M, the 915 MHz, 250 A version",
        source: "Milesight product page",
      },
      { label: "Measures", value: "RMS current on one conductor" },
      { label: "Rated current", value: "250 A", source: MILESIGHT_DATASHEET },
      { label: "Wire hole", value: "16 mm", source: "Milesight product page" },
      { label: "Radio", value: "LoRaWAN, Class A, AU915 among the listed plans", source: MILESIGHT_DATASHEET },
      { label: "Enclosure", value: "IP30", source: MILESIGHT_DATASHEET },
      {
        label: "Decoded current field",
        value: "current, in amperes",
        source: "Milesight CT103 decoder, published script",
      },
    ],
    claimed: [
      {
        feature: "Self-powered from the conductor, or 5 V on the USB-C port",
        soWhat:
          "Below a few amperes the clamp can stop reporting when it is powered only by the cable. Milesight's page puts that floor near 3 A at a one-minute interval and 2.8 A at ten minutes. A reseller page quotes different floors. Treat the figure as to verify.",
      },
      {
        feature: "Optional NTC on the cable, −20 °C to 100 °C",
        soWhat:
          "Stored and shown when the uplink carries it. It is the cable, not the room, and it does not open a temperature incident.",
      },
      {
        feature: "Accumulated ampere-hours",
        soWhat:
          "The decoder emits total_current. This platform leaves that counter in the raw payload. It is not an electricity bill.",
      },
    ],
    platformBehaviour: [
      { label: "Device type to register", value: "Current on one cable" },
      {
        label: "Stored per reading",
        value: "currentA in amperes, plus temperatureC only when the uplink carries it",
      },
      {
        label: "What raises an alert",
        value:
          "Current outside the circuit's ampere band for ten minutes. No band means no verdict. Cable temperature does not raise the room-temperature alert.",
      },
      {
        label: "Reporting assumption",
        value:
          "Device configuration starts at 600 seconds, and silence waits 30 minutes. The device list still marks offline at 15 minutes.",
      },
      {
        label: "Codec assumption",
        value:
          "Official Milesight key `current`. Confirmed against the published decoder, not against a unit here.",
      },
    ],
    toVerify: [
      {
        topic: "Semakiot listing, SKU, stock and landed price",
        why: "No public Semakiot page for the CT103 was confirmed on this pass. None of it may be quoted",
        answeredBy: "Semakiot",
      },
      {
        topic: "A real uplink on the pilot application",
        why: "Confirms `current` and whether a dry or failed read omits the field",
        answeredBy: "A commissioned unit",
      },
      {
        topic: "Minimum current that still powers a transmission",
        why: "The manufacturer page and one reseller page do not agree",
        answeredBy: "Milesight, then a unit",
      },
      {
        topic: "JoinEUI printed on the unit",
        why: "No vendor default is registered, so the technician enters the one on the label",
        answeredBy: "The label",
      },
    ],
  },
];

/**
 * One entry by id, for the detail page.
 *
 * The id is in the URL, so it arrives as an arbitrary string and the lookup
 * has to be able to fail. Returning `undefined` rather than throwing lets the
 * page render a not-found instead of the router swallowing an exception.
 */
export function findCatalogueEntry(id: string): CatalogueEntry | undefined {
  return CATALOGUE.find((entry) => entry.id === id);
}
